import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseclient";
import { useNavigate } from "react-router-dom";

function EditProfile() {
  const [city, setCity] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentResume, setCurrentResume] = useState(null);
  const navigate = useNavigate();

  // Load current profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user) {
          navigate("/sign-in");
          return;
        }

        // Fetch current profile
        const { data: profile, error } = await supabase
          .from("users")
          .select("city, resume_url")
          .eq("id", session.user.id)
          .single();

        if (error) {
          console.error("Error loading profile:", error);
          alert("Failed to load profile. Please try again.");
          navigate("/profile");
          return;
        }

        if (profile) {
          setCity(profile.city || "");
          setCurrentResume(profile.resume_url);
        }
      } catch (err) {
        console.error("Error loading profile:", err);
        alert("Failed to load profile. Please try again.");
        navigate("/profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleFileChange = (e) => {
    setResumeFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      alert("User not found. Please sign in again.");
      setUploading(false);
      return;
    }

    let resumeUrl = currentResume; // Keep existing resume URL if no new file uploaded

    if (resumeFile) {
      const fileExt = resumeFile.name.split(".").pop();
      const filePath = `${user.id}/resume.${fileExt}`;

      // Upload new resume
      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(filePath, resumeFile, { upsert: true });

      if (uploadError) {
        console.error("❌ Upload failed:", uploadError.message);
        alert("Failed to upload resume: " + uploadError.message);
        setUploading(false);
        return;
      }

      // Get the public URL
      const { data: urlData, error: urlError } = supabase.storage
        .from("resumes")
        .getPublicUrl(filePath);

      if (urlError) {
        console.error("Failed to get public URL:", urlError.message);
      }

      resumeUrl = urlData?.publicUrl;
    }

    // Update only city and resume_url (not full_name or profile_created)
    const updateData = {
      city: city,
    };

    // Only update resume_url if a new file was uploaded
    if (resumeFile) {
      updateData.resume_url = resumeUrl;
    }

    const { error: updateError } = await supabase
      .from("users")
      .update(updateData)
      .eq("id", user.id)
      .select();

    if (updateError) {
      console.error("❌ Update error:", updateError.message);
      alert("Failed to update profile: " + updateError.message);
      setUploading(false);
      return;
    }

    // Success - redirect back to profile
    navigate("/profile");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔄</div>
          <div>Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #e9ecef",
          padding: "1rem 0",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "0 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1
            style={{
              color: "#2c3e50",
              fontSize: "1.8rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            ✏️ Edit Profile
          </h1>
          <button
            onClick={() => navigate("/profile")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.9rem",
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      <div
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "0 2rem 2rem",
        }}
      >
        {/* Profile Form */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  color: "#2c3e50",
                  fontSize: "1rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                City
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter your city (e.g., San Francisco, Los Angeles)"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
              />
              <p
                style={{
                  color: "#6c757d",
                  fontSize: "0.85rem",
                  margin: "0.5rem 0 0 0",
                }}
              >
                This helps us show you relevant local opportunities
              </p>
            </div>

            <div style={{ marginBottom: "2rem" }}>
              <label
                style={{
                  display: "block",
                  color: "#2c3e50",
                  fontSize: "1rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Resume (PDF)
              </label>
              {currentResume && !resumeFile && (
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#e8f5e8",
                    borderRadius: "6px",
                    border: "1px solid #d4edda",
                    marginBottom: "1rem",
                  }}
                >
                  <p
                    style={{
                      color: "#155724",
                      margin: 0,
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    ✅ Current resume is uploaded
                  </p>
                </div>
              )}
              <div
                style={{
                  border: "2px dashed #e9ecef",
                  borderRadius: "6px",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: "#f8f9fa",
                  transition: "border-color 0.2s ease",
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  📄
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{
                    display: "none",
                  }}
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  style={{
                    display: "inline-block",
                    backgroundColor: "#007bff",
                    color: "white",
                    padding: "0.75rem 1.5rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  {currentResume ? "Update Resume" : "Upload Resume"}
                </label>
                {resumeFile && (
                  <div
                    style={{
                      color: "#27ae60",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      marginTop: "0.5rem",
                    }}
                  >
                    ✅ {resumeFile.name}
                  </div>
                )}
                <p
                  style={{
                    color: "#6c757d",
                    fontSize: "0.85rem",
                    margin: "0.5rem 0 0 0",
                  }}
                >
                  {currentResume
                    ? "Upload a new resume to replace your current one"
                    : "Upload your resume in PDF format"}
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading}
              style={{
                width: "100%",
                padding: "1rem",
                backgroundColor: uploading ? "#6c757d" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: uploading ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "1rem",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#0056b3";
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#007bff";
                }
              }}
            >
              {uploading ? "Updating Profile..." : "Save Changes"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditProfile;

